import type { KaraokeConfig } from './KaraokeConfig';

/**
 * PLAT-10788 — clone a karaoke config map (fresh `Map` + a shallow copy of each
 * config) so the editor's live configs and the per-language cache
 * (`KaraokeStore.loadedKaraokeDataByLanguage`) never share references.
 *
 * Invariant: whichever side takes ownership of a config map must own a private
 * copy. Without it, an in-place config mutation on one side (e.g.
 * `applyCaptionsLanguage` switching the language via `karaokeConfigs.set`)
 * rewrites the other side's variant in place — a saved **source** variant
 * ending up with a **translation's** `language`. Applied at both boundaries:
 * on capture (`KaraokeStore.getKaraokeData`) and on load
 * (`VideoEditor.loadKaraokeData`).
 */
export function cloneKaraokeConfigs(
  configs: Map<string, KaraokeConfig>,
): Map<string, KaraokeConfig> {
  return new Map(
    Array.from(configs.entries()).map(([id, config]) => [
      id,
      {
        ...config,
        // PLAT-11409 — the substitution marker is the config's first
        // object-valued field; copy it too so the no-shared-references
        // invariant above stays true.
        ...(config.font_before_substitution
          ? {
              font_before_substitution: {
                ...config.font_before_substitution,
              },
            }
          : {}),
      },
    ]),
  );
}
